import { format, parseISO } from "date-fns";

import { PROCEDURE_CODE_EFFECTIVE_WINDOWS } from "../constants/procedure-code-effective-dates";
import { RETIRED_PROCEDURE_CODES } from "../constants/retired-procedure-codes";

function formatWindowDate(date: string): string {
  return format(parseISO(date), "M/d/yyyy");
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

declare const serviceDateKeyBrand: unique symbol;

/**
 * A service date as the service's local `yyyy-MM-dd`, the only form the date rules accept. Build
 * one with toServiceDateKey (or getEncounterServiceDateKey for an encounter), so a dateTime, which
 * would compare on its UTC day near midnight, cannot reach the rules.
 */
export type ServiceDateKey = string & { readonly [serviceDateKeyBrand]: true };

/**
 * Checks a value is a local `yyyy-MM-dd` service date.
 *
 * @param value A service date already in the service's local time
 * @returns The service date key, or undefined for anything else (such as a dateTime), so no date
 *   rule applies
 */
export function toServiceDateKey(value: string | undefined): ServiceDateKey | undefined {
  return value && DATE_KEY_PATTERN.test(value) ? (value as ServiceDateKey) : undefined;
}

/**
 * Whether a code may be offered for a service date. Retired codes are never offered, and codes
 * past their last billable date are not offered for later dates. An unknown service date applies
 * no date rule.
 *
 * @param code Procedure code
 * @param serviceDate Service date as `yyyy-MM-dd` in the encounter's timezone
 */
export function isProcedureCodeOfferedOn(
  code: string,
  serviceDate: ServiceDateKey | undefined,
): boolean {
  if (RETIRED_PROCEDURE_CODES.has(code)) return false;
  const effectiveThrough = PROCEDURE_CODE_EFFECTIVE_WINDOWS[code]?.effectiveThrough;
  return !serviceDate || !effectiveThrough || serviceDate <= effectiveThrough;
}

/**
 * The warning for a code selected outside its billable window: before it takes effect, or after
 * its last billable date. An unknown service date applies no rule.
 *
 * @param code Procedure code
 * @param serviceDate Service date as `yyyy-MM-dd` in the encounter's timezone
 * @returns The warning text, or undefined when the code is billable on that date
 */
export function getProcedureCodeWarningOn(
  code: string,
  serviceDate: ServiceDateKey | undefined,
): string | undefined {
  const window = PROCEDURE_CODE_EFFECTIVE_WINDOWS[code];
  if (!serviceDate || !window) return undefined;

  if (window.effectiveFrom && serviceDate < window.effectiveFrom) {
    return `This code is effective ${formatWindowDate(window.effectiveFrom)}. Claims for earlier service dates will likely be rejected.`;
  }
  if (window.effectiveThrough && serviceDate > window.effectiveThrough) {
    return `This code is billable for service dates through ${formatWindowDate(window.effectiveThrough)}. Claims for later service dates will likely be rejected.`;
  }
  return undefined;
}

/** Label for a code that becomes billable on a future date, e.g. "Effective 1/1/2027". */
export function getProcedureCodeEffectiveLabel(code: string): string | undefined {
  const effectiveFrom = PROCEDURE_CODE_EFFECTIVE_WINDOWS[code]?.effectiveFrom;
  return effectiveFrom ? `Effective ${formatWindowDate(effectiveFrom)}` : undefined;
}
